/**
 * API Service for Django Backend
 * Handles all API calls to Django REST endpoints
 */
import axios from 'axios';
import { formatWhatsAppErrorMessage, formatWhatsAppSuccessMessage } from '../utils/whatsappMessaging';
import { notifyBookingMutation } from '../utils/bookingSync';
import { downloadBlob, ensurePdfBlob, parseContentDispositionFilename } from '../utils/downloadBlob';
import { isNexaStayPublicHost, isNexaStayPublicPathname } from '../nexastay/useNexaStayBasePath';
import { getOrFetchBookingGuestDocuments } from '../utils/bookingGuestDocumentsCache';

const API_BASE_URL = '';

// Get CSRF token from cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

function getBearerTokenHeader() {
  if (typeof window === 'undefined') return {};
  const token =
    window.localStorage.getItem('access_token')
    || window.localStorage.getItem('authToken')
    || window.localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add CSRF token to requests
api.interceptors.request.use((config) => {
  const csrftoken = getCookie('csrftoken');
  if (csrftoken) {
    config.headers['X-CSRFToken'] = csrftoken;
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error logging
    const errorDetails = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
    };
    
    // Log detailed error information
    
    // Enhance error object with more details
    if (error.response) {
      const status = error.response.status;
      // Server responded with error status
      const responseData = error.response.data;
      const detailText = String(responseData?.detail || '').toLowerCase();
      const errorText = String(responseData?.error || '').toLowerCase();

      // Redirect only for actual auth failures (avoid forcing login on business-rule 403s)
      const isAuthFailure =
        status === 401 ||
        (status === 403 &&
          (
            detailText.includes('authentication credentials were not provided') ||
            detailText.includes('not authenticated') ||
            detailText.includes('invalid token') ||
            detailText.includes('token')
          ));
      if (isAuthFailure) {
        const requestUrl = String(error.config?.url || '');
        const onNexaStayPublicSite =
          isNexaStayPublicHost()
          || isNexaStayPublicPathname(window.location.pathname)
          || requestUrl.includes('/nexastay/');
        if (onNexaStayPublicSite) {
          return Promise.reject(error);
        }
        const nextUrl = window.location.pathname + window.location.search;
        const loginUrl = nextUrl && nextUrl !== '/' && nextUrl !== '/login/'
          ? `/login/?next=${encodeURIComponent(nextUrl)}`
          : '/login/';
        window.location.href = loginUrl;
        return Promise.reject(error);
      }

      // Hotel context errors should guide user to hotel selection, not login
      const isHotelSelectionFailure =
        status === 400 || status === 403
          ? (
            errorText.includes('no hotel selected') ||
            errorText.includes('user profile is incomplete') ||
            errorText.includes('user has no associated hotels') ||
            errorText.includes('user profile not found')
          )
          : false;
      if (isHotelSelectionFailure) {
        window.location.href = '/onboarding/landing/';
        return Promise.reject(error);
      }
      
      // Extract error message from response
      let errorMessage = 'An error occurred';
      if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (responseData?.error) {
        const err = responseData.error;
        // Handle __all__ or nested validation error format
        if (typeof err === 'object' && err !== null && err['__all__']) {
          const msgs = err['__all__'];
          errorMessage = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
        } else {
          errorMessage = typeof err === 'string' ? err : JSON.stringify(err);
        }
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else if (responseData?.detail) {
        errorMessage = responseData.detail;
      } else if (responseData?.errors) {
        // Handle validation errors
        const errObj = responseData.errors;
        if (errObj && typeof errObj === 'object' && errObj['__all__']) {
          const msgs = errObj['__all__'];
          errorMessage = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
        } else {
          const errorMessages = Object.entries(errObj || {})
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (responseData && typeof responseData === 'object') {
        // Handle plain field-level validation payloads e.g. { name: ["Already exists"] }
        const errorMessages = Object.entries(responseData)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        errorMessage = errorMessages || errorMessage;
      }
      
      // Add enhanced error info
      error.userMessage = errorMessage;
      error.statusCode = error.response.status;
      error.responseData = responseData;
    } else if (error.request) {
      // Request was made but no response received
      error.userMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
    } else {
      // Something else happened
      error.userMessage = error.message || 'An unexpected error occurred';
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get available room types for a date range
 * @param {string} checkInDate - YYYY-MM-DD
 * @param {string} checkOutDate - YYYY-MM-DD
 * @param {object} config - { signal, exclude_booking_id }
 */
export const getAvailableRoomTypes = async (checkInDate, checkOutDate, config = {}) => {
  try {
    const { exclude_booking_id, ...restConfig } = config;
    const params = { check_in_date: checkInDate, check_out_date: checkOutDate };
    if (exclude_booking_id != null) params.exclude_booking_id = exclude_booking_id;
    const response = await api.get('/booking/api/available-room-types/', {
      params,
      ...restConfig,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get rates for a room type and date based on meal plan
 */
/**
 * Get all rate plans (meal plans) configured for the hotel
 */
export const getHotelRatePlans = async (config = {}) => {
  try {
    const response = await api.get('/booking/api/room-type-rate-plans/', config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getRatesByRatePlan = async (roomTypeId, checkInDate, ratePlanCode, checkOutDate = null) => {
  try {
    const params = {
      room_type_id: roomTypeId,
      check_in_date: checkInDate,
      rate_plan_code: ratePlanCode,
    };
    if (checkOutDate) params.check_out_date = checkOutDate;
    const response = await api.get('/booking/api/rates-by-meal-plan/', {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};


/**
 * Submit reservation
 */
export const submitReservation = async (reservationData) => {
  try {
    const idempotencyKey = reservationData?.idempotencyKey || reservationData?.idempotency_key;
    const config = idempotencyKey
      ? { headers: { 'X-Idempotency-Key': idempotencyKey } }
      : undefined;
    const response = await api.post('/booking/api/reservation/submit/', reservationData, config);
    const payload = response.data || {};
    notifyBookingMutation({
      type: 'reservation_created',
      bookingId: payload.booking_id ?? payload.id ?? null,
      bookingCode: payload.booking_code ?? payload.code ?? null,
      checkInDate: payload.check_in_date ?? reservationData?.checkInDate ?? null,
      checkOutDate: payload.check_out_date ?? reservationData?.checkOutDate ?? null,
    });
    return payload;
  } catch (error) {
    throw error;
  }
};

/**
 * Submit walk-in booking
 */
export const submitWalkIn = async (walkInData) => {
  try {
    const idempotencyKey = walkInData?.idempotencyKey || walkInData?.idempotency_key;
    const config = idempotencyKey
      ? { headers: { 'X-Idempotency-Key': idempotencyKey } }
      : undefined;
    const response = await api.post('/booking/api/walkin/submit/', walkInData, config);
    const payload = response.data || {};
    const bookings = Array.isArray(payload.bookings) ? payload.bookings : [];
    const firstBookingId = bookings[0]?.booking_id ?? payload.booking_id ?? payload.id ?? null;
    notifyBookingMutation({
      type: payload.quick_booking ? 'quick_book' : 'walk_in',
      bookingId: firstBookingId,
      totalBookings: payload.total_bookings ?? bookings.length,
      checkInDate: bookings[0]?.check_in_date ?? walkInData?.checkInDate ?? null,
      checkOutDate: bookings[0]?.check_out_date ?? walkInData?.checkOutDate ?? null,
    });
    return payload;
  } catch (error) {
    throw error;
  }
};

/**
 * AI walk-in: extract guest fields from ID image(s). Multipart `document` (required) and optional `document_back`.
 */
export const extractWalkInIdDocument = async ({
  front,
  back = null,
  frontFilename = 'id-front.jpg',
  backFilename = 'id-back.jpg',
} = {}) => {
  if (!front) {
    throw new Error('Front image is required');
  }
  const formData = new FormData();
  formData.append('document', front, frontFilename);
  if (back) {
    formData.append('document_back', back, backFilename);
  }
  const response = await api.post('/booking/api/walkin/extract-id-document/', formData, {
    headers: {
      ...getBearerTokenHeader(),
    },
    transformRequest: [(data, headers) => {
      if (data instanceof FormData) {
        delete headers['Content-Type'];
      }
      return data;
    }],
  });
  return response.data;
};

export const createWebCheckInInvite = async (payload) => {
  const response = await api.post('/booking/api/web-checkin/invite/', payload);
  return response.data;
};

export const getWebCheckInInvites = async (params = {}) => {
  const response = await api.get('/booking/api/web-checkin/invites/', { params });
  return response.data;
};

export const getWebCheckInInviteDetail = async (inviteId) => {
  const response = await api.get(`/booking/api/web-checkin/invites/${encodeURIComponent(inviteId)}/`);
  return response.data;
};

export const updateWebCheckInInvite = async (inviteId, payload = {}) => {
  const response = await api.patch(
    `/booking/api/web-checkin/invites/${encodeURIComponent(inviteId)}/`,
    payload
  );
  return response.data;
};

export const getPublicWebCheckInInvite = async (token) => {
  const response = await api.get(`/booking/api/web-checkin/${encodeURIComponent(token)}/`);
  return response.data;
};

export const extractPublicWebCheckInIdDocument = async (
  token,
  {
    front,
    back = null,
    frontFilename = 'id-front.jpg',
    backFilename = 'id-back.jpg',
  } = {}
) => {
  if (!front) {
    throw new Error('Front image is required');
  }
  const formData = new FormData();
  formData.append('document', front, frontFilename);
  if (back) {
    formData.append('document_back', back, backFilename);
  }
  const response = await api.post(
    `/booking/api/web-checkin/${encodeURIComponent(token)}/extract-id-document/`,
    formData,
    {
      transformRequest: [(data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      }],
    }
  );
  return response.data;
};

export const submitPublicWebCheckIn = async (token, payload) => {
  const response = await api.post(
    `/booking/api/web-checkin/${encodeURIComponent(token)}/submit/`,
    payload
  );
  const data = response.data || {};
  notifyBookingMutation({
    type: 'web_checkin',
    bookingId: data.booking?.booking_id ?? null,
    bookingCode: data.booking?.booking_code ?? null,
    checkInDate: data.booking?.check_in_date ?? null,
    checkOutDate: data.booking?.check_out_date ?? payload?.checkOutDate ?? null,
  });
  return data;
};

export const startPublicWebCheckInPayment = async (token, payload) => {
  const response = await api.post(
    `/booking/api/web-checkin/${encodeURIComponent(token)}/start-payment/`,
    payload
  );
  return response.data;
};

export const verifyPublicWebCheckInPayment = async (token, payload) => {
  const response = await api.post(
    `/booking/api/web-checkin/${encodeURIComponent(token)}/verify-payment/`,
    payload
  );
  const data = response.data || {};
  notifyBookingMutation({
    type: 'web_checkin',
    bookingId: data.booking?.booking_id ?? null,
    bookingCode: data.booking?.booking_code ?? null,
    checkInDate: data.booking?.check_in_date ?? null,
    checkOutDate: data.booking?.check_out_date ?? null,
  });
  return data;
};

/**
 * Check tax rules availability
 */
export const getTaxRulesAvailability = async (currency = 'INR') => {
  try {
    const response = await api.get('/booking/api/tax-rules-availability/', {
      params: { currency }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get booking list data
 * Uses DRF API endpoint which returns JSON by default
 */
export const getBookingList = async (params = {}) => {
  try {
    const response = await api.get('/booking/api/list/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get group booking list (multi date-range reservations)
 */
export const getGroupBookingList = async (params = {}) => {
  const response = await api.get('/booking/api/group-bookings/', { params });
  return response.data;
};

/**
 * Get group booking detail with member bookings
 */
export const getGroupBookingDetails = async (groupBookingId, config = {}) => {
  const parsedId = Number(groupBookingId);
  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    throw new Error('Invalid group booking reference');
  }
  const response = await api.get(`/booking/api/group-bookings/${parsedId}/`, config);
  return response.data;
};

/**
 * Get bookings for check-in by date (supports page / per_page for arrival board)
 * @param {string} checkInDate YYYY-MM-DD
 * @param {{ page?: number, per_page?: number }} [options]
 */
export const getCheckInBookings = async (checkInDate, options = {}) => {
  try {
    const params = {
      check_in_date: checkInDate,
    };
    if (options.page != null) params.page = options.page;
    if (options.per_page != null) params.per_page = options.per_page;
    const response = await api.get('/booking/api/checkin-bookings/', {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get bookings that are currently staying
 */
export const getStayingBookings = async (date, options = {}) => {
  try {
    const params = { date };
    if (options.includePendingCheckout) {
      params.include_pending_checkout = 1;
      if (options.pendingLookbackDays !== undefined && options.pendingLookbackDays !== null) {
        params.pending_lookback_days = options.pendingLookbackDays;
      }
    }
    const response = await api.get('/booking/api/staying-bookings/', {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get bookings ready for checkout
 */
export const getCheckOutBookings = async (date, options = {}) => {
  try {
    const params = { date };
    if (options.includePendingCheckout) {
      params.include_pending_checkout = 1;
      if (options.pendingLookbackDays !== undefined && options.pendingLookbackDays !== null) {
        params.pending_lookback_days = options.pendingLookbackDays;
      }
    }
    const response = await api.get('/booking/api/checkout-bookings/', {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get allocated rooms for a booking (for checkout)
 */
export const getCheckOutRooms = async (bookingId) => {
  try {
    const response = await api.get('/booking/api/checkout/rooms/', {
      params: { booking_id: bookingId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Process checkout for a booking
 */
export const processCheckOut = async (bookingId, roomIds, paymentData) => {
  try {
    const response = await api.post('/booking/api/checkout/process/', {
      booking_id: bookingId,
      room_ids: roomIds,
      payment: paymentData,
    });
    notifyBookingMutation({ type: 'checkout', bookingId });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get bookings that are "no show" - checkout date has passed 11 AM
 */
export const getNoShowBookings = async (date) => {
  try {
    const response = await api.get('/booking/api/no-show-bookings/', {
      params: { date: date },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get confirmed bookings for room allocation (check-in date)
 */
export const getRoomAllocationBookings = async (date) => {
  try {
    const response = await api.get('/booking/api/room-allocation-bookings/', {
      params: { date: date },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get available rooms for room allocation (booking date range).
 * @param {number|string} bookingId
 * @param {{ include_room_type_ids?: number[], active_only?: boolean }} options
 * include_room_type_ids: room type IDs to include (e.g. for upgrade flow)
 * active_only: when true, API returns only checkout=false booking items
 */
export const getRoomAllocationAvailableRooms = async (bookingId, options = {}) => {
  try {
    const params = { booking_id: bookingId };
    if (options.include_room_type_ids?.length) {
      params.include_room_type_ids = options.include_room_type_ids.join(',');
    }
    if (options.active_only === true) {
      params.active_only = true;
    }
    const response = await api.get('/booking/api/room-allocation/available-rooms/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Assign rooms to a booking (room allocation)
 */
export const assignRooms = async (bookingId, roomAllocations) => {
  try {
    const response = await api.post('/booking/api/room-allocation/assign/', {
      booking_id: bookingId,
      room_allocations: roomAllocations,
    });
    notifyBookingMutation({ type: 'room_allocation', bookingId });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get available rooms for check-in
 */
export const getCheckInAvailableRooms = async (bookingId, config = {}) => {
  try {
    const response = await api.get('/booking/api/checkin/available-rooms/', {
      params: { booking_id: bookingId },
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Process check-in for a booking
 */
export const processCheckIn = async (
  bookingId,
  roomAllocations,
  paymentData = null,
  purposeFields = null
) => {
  try {
    const payload = {
      booking_id: bookingId,
      room_allocations: roomAllocations,
    };
    
    // Add payment data if provided - use 'payment_data' to match backend serializer
    if (paymentData) {
      payload.payment_data = paymentData;
    }
    if (purposeFields && typeof purposeFields === 'object') {
      if (purposeFields.purpose_of_stay != null) {
        payload.purpose_of_stay = purposeFields.purpose_of_stay;
      }
      if (purposeFields.purpose_of_stay_note != null) {
        payload.purpose_of_stay_note = purposeFields.purpose_of_stay_note;
      }
    }
    
    
    const response = await api.post('/booking/api/checkin/process/', payload);
    notifyBookingMutation({ type: 'checkin', bookingId });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get payment list data
 * Uses DRF API endpoint which returns JSON by default
 */
export const getPaymentList = async (params = {}) => {
  try {
    const response = await api.get('/finance/api/payments/', { params });
    return response.data;
  } catch (error) {
    // Error fetching payment list:', error);
    throw error;
  }
};

export const getOtaCommissionList = async (params = {}) => {
  const response = await api.get('/finance/api/ota-commissions/', { params });
  return response.data;
};

/** GST report rows for finance reports hub */
export const getGstReport = async (params = {}) => {
  try {
    const response = await api.get('/finance/api/gst-report/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Police report guest rows (Booking → Room → Guest) for reports hub */
export const getPoliceReport = async (params = {}) => {
  try {
    const response = await api.get('/booking/api/police-report/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get POS restaurant orders for reporting
 */
export const getPosRestaurantOrders = async (params = {}) => {
  try {
    const response = await api.get('/pos-restaurant/api/orders/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get POS restaurant payment entries for reporting
 */
export const getPosOrderPayments = async (params = {}) => {
  try {
    const response = await api.get('/pos-restaurant/api/payments/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get customer list data
 * Uses DRF API endpoint which returns JSON by default
 */
export const getCustomerList = async (params = {}, config = {}) => {
  try {
    const response = await api.get('/customerhub/api/customers/', { params, ...config });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getRetentionCampaign = async (params = {}) => {
  const response = await api.get('/customerhub/api/retention/campaign/', { params });
  return response.data;
};

export const previewRetentionMessage = async (payload = {}) => {
  const response = await api.post('/customerhub/api/retention/preview/', payload);
  return response.data;
};

export const generateRetentionMessage = async (payload = {}) => {
  const response = await api.post('/customerhub/api/retention/generate-message/', payload);
  return response.data;
};

/**
 * Get inventory list data
 * Uses DRF API endpoint which returns JSON by default
 */
export const getInventoryList = async (params = {}) => {
  try {
    const response = await api.get('/inventory/api/list/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getInventoryListDateDetail = async (date) => {
  try {
    const response = await api.get('/inventory/api/list/detail/', {
      params: { date },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get inventory slots for hourly booking
 */
export const getInventorySlots = async (checkInDate, checkOutDate) => {
  try {
    const response = await api.get('/inventory/api/slots/', {
      params: {
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
      },
    });
    return response.data;
  } catch (error) {
    // Error fetching inventory slots:', error);
    throw error;
  }
};

/**
 * Get inventory data for calendar view
 * Uses DRF API endpoint which returns JSON by default
 */
const inFlightInventoryRequests = new Map();

export const getInventoryData = async (startDate, endDate, channelId = 'pms', bypassCache = false) => {
  const normalizedChannel = channelId || 'pms';
  const requestKey = `${startDate}|${endDate}|${normalizedChannel}|${bypassCache ? 'bypass' : 'cached'}`;

  if (inFlightInventoryRequests.has(requestKey)) {
    return inFlightInventoryRequests.get(requestKey);
  }

  const requestPromise = (async () => {
    try {
      const params = {
          action: 'list',
          start_date: startDate,
          end_date: endDate,
      };
      
      if (normalizedChannel === 'all') {
        params.all_channels = 1;
      } else if (normalizedChannel !== 'pms') {
        params.channel_id = normalizedChannel;
      }
      
      // Add cache-busting parameter if needed to force fresh data
      if (bypassCache) {
        params._t = Date.now();
      }
      
      const response = await api.get('/inventory/api/manage/', { params });
      return response.data;
    } catch (error) {
      // Error fetching inventory data:', error);
      throw error;
    } finally {
      inFlightInventoryRequests.delete(requestKey);
    }
  })();

  inFlightInventoryRequests.set(requestKey, requestPromise);
  return requestPromise;
};

/**
 * Get inventory for a specific date
 * Uses DRF API endpoint which returns JSON by default
 */
export const getInventoryForDate = async (date, roomTypeId, channelId = 'pms', ratePlanId = null) => {
  try {
    const params = {
        action: 'get_inventory',
        date: date,
        room_type_id: roomTypeId,
    };
    
    // Add channel parameter if not PMS
    if (channelId && channelId !== 'pms') {
      params.channel_id = channelId;
    }
    
    // Add rate plan if provided
    if (ratePlanId) {
      params.rate_plan_id = ratePlanId;
    }
    
    const response = await api.get('/inventory/api/manage/', { params });
    return response.data;
  } catch (error) {
    // Error fetching inventory:', error);
    throw error;
  }
};

/**
 * Save inventory data
 * Uses DRF API endpoint which handles POST requests directly
 */
export const saveInventory = async (inventoryData) => {
  try {
    const payload = {
      ...inventoryData,
    };
    
    // Include channel_id if provided (even if 'pms')
    if (inventoryData.channel_id) {
      payload.channel_id = inventoryData.channel_id;
    }
    
    const response = await api.post('/inventory/api/manage/', payload);
    return response.data;
  } catch (error) {
    // Error saving inventory:', error);
    throw error;
  }
};

/**
 * Bulk save inventory for multiple dates
 * Uses bulk update endpoint to update multiple dates in a single API call
 */
export const bulkSaveInventory = async (updates) => {
  try {
    const payload = {
      updates: updates.map(update => ({
        date: update.date,
        end_date: update.end_date || null,
        room_type_id: update.room_type_id,
        channel_id: update.channel_id || null,
        total_rooms: update.total_rooms || null,
        allocated_rooms: update.allocated_rooms || null,
        available: update.available !== undefined ? update.available : true,
        sold_out: update.sold_out !== undefined ? update.sold_out : false,
        reason: update.reason || '',
        block_type: update.block_type || null,
      })),
    };
    
    const response = await api.post('/inventory/api/manage/?action=bulk', payload);
    return response.data;
  } catch (error) {
    // Error bulk saving inventory:', error);
    throw error;
  }
};

/**
 * Set channel availability for a date range (same payload as sync: start_date, end_date, room_type_id(s), channel_id).
 * Updates DB for the range then pushes to ChannelHub in one go.
 * roomTypeId: number|string|array — single id or list of ids (Select All room types).
 * operation: 'available' | 'blocked' | 'sold_out' | 'unblocked'
 * channelId: 'pms' | 'all' | single id — or pass channelIds for specific OTAs (Stop Sell / Available).
 */
export const setAvailabilityRange = async (
  startDate,
  endDate,
  roomTypeId,
  channelId,
  operation = 'available',
  { channelIds = null } = {}
) => {
  try {
    const params = {
      action: 'set_availability',
      start_date: startDate,
      end_date: endDate,
      operation,
    };
    const specificIds = Array.isArray(channelIds)
      ? channelIds.filter((id) => id != null && id !== '' && id !== 'pms' && id !== 'all')
      : [];
    if (specificIds.length > 0) {
      params.channel_ids = specificIds.join(',');
    } else if (channelId != null && channelId !== '') {
      params.channel_id = channelId;
    }
    const ids = Array.isArray(roomTypeId)
      ? roomTypeId.filter((id) => id != null && id !== '' && id !== 'all')
      : roomTypeId != null && roomTypeId !== '' && roomTypeId !== 'all'
        ? [roomTypeId]
        : [];
    if (ids.length > 1) {
      params.room_type_ids = ids.join(',');
    } else if (ids.length === 1) {
      params.room_type_id = ids[0];
    }
    const response = await api.post('/inventory/api/manage/', null, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Apply/clear inventory restrictions via Aiosell inventory-restrictions-push.
 * Does not set stopSell — use setAvailabilityRange(..., 'sold_out') for Stop Sell.
 * channelId: 'pms' | 'all' | single id — or pass channelIds for specific OTAs (toChannels).
 * restrictions: full field object from the Restrictions drawer (CTA/CTD + stay/advance).
 */
export const setRestrictionsRange = async (
  startDate,
  endDate,
  roomTypeId,
  channelId,
  {
    blockType = 'master',
    apply = true,
    reason = '',
    channelIds = null,
    restrictions = null,
  } = {}
) => {
  try {
    const params = {
      action: 'set_restrictions',
      start_date: startDate,
      end_date: endDate,
      block_type: blockType,
      apply: apply ? 'true' : 'false',
    };
    if (reason) params.reason = reason;
    const specificIds = Array.isArray(channelIds)
      ? channelIds.filter((id) => id != null && id !== '' && id !== 'pms' && id !== 'all')
      : [];
    if (specificIds.length > 0) {
      params.channel_ids = specificIds.join(',');
    } else if (channelId != null && channelId !== '') {
      params.channel_id = channelId;
    }
    const ids = Array.isArray(roomTypeId)
      ? roomTypeId.filter((id) => id != null && id !== '' && id !== 'all')
      : roomTypeId != null && roomTypeId !== '' && roomTypeId !== 'all'
        ? [roomTypeId]
        : [];
    if (ids.length > 1) {
      params.room_type_ids = ids.join(',');
    } else if (ids.length === 1) {
      params.room_type_id = ids[0];
    }
    const body =
      restrictions && typeof restrictions === 'object'
        ? { restrictions }
        : {};
    const response = await api.post('/inventory/api/manage/', body, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get booking calendar data
 */
export const getBookingCalendarData = async (
  startDate,
  endDate,
  viewType = 'week',
  { refresh = false } = {}
) => {
  try {
    const params = {
      start_date: startDate,
      end_date: endDate,
      view: viewType,
    };
    if (refresh) {
      params.refresh = '1';
    }
    const response = await api.get('/dashboard/api/booking-calendar-data/', {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Aggregated pending tasks for dashboard rail (checkout, check-in, housekeeping, etc.)
 */
export const getDashboardPendingTasks = async (date, { refresh = false } = {}) => {
  try {
    const params = {};
    if (date) {
      params.date = date;
    }
    if (refresh) {
      params.refresh = '1';
    }
    const response = await api.get('/dashboard/api/pending-tasks/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get booking details by ID
 */
const getValidBookingIdOrThrow = (bookingId) => {
  const parsedId = Number(bookingId);
  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    throw new Error('Invalid booking reference');
  }
  return parsedId;
};

export const getBookingDetails = async (bookingId, config = {}) => {
  try {
    const safeBookingId = getValidBookingIdOrThrow(bookingId);
    const response = await api.get(`/booking/api/${safeBookingId}/`, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get booking audit events by booking ID.
 */
export const getBookingAuditEvents = async (bookingId, params = {}) => {
  try {
    const safeBookingId = getValidBookingIdOrThrow(bookingId);
    const response = await api.get(`/booking/api/${safeBookingId}/audit-events/`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update booking with comprehensive data (PUT)
 * Used for full booking edit page. Calls dedicated edit API to avoid affecting other flows.
 */
export const updateBooking = async (bookingId, bookingData) => {
  try {
    const safeBookingId = getValidBookingIdOrThrow(bookingId);
    const response = await api.put(`/booking/api/${safeBookingId}/edit/`, bookingData);
    notifyBookingMutation({
      type: 'booking_updated',
      bookingId: safeBookingId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Add incremental extra charges to an existing booking (payment drawers).
 */
export const addBookingExtraCharges = async (bookingId, { amount, note = '', gstPercent = 0 } = {}) => {
  const safeBookingId = getValidBookingIdOrThrow(bookingId);
  const response = await api.post(`/booking/api/${safeBookingId}/extra-charges/`, {
    extraChargesAmount: amount,
    extraChargesNote: note,
    extraChargesReason: note,
    reason: note,
    extraChargesGstPercent: gstPercent,
  });
  notifyBookingMutation({
    type: 'booking_updated',
    bookingId: safeBookingId,
  });
  return response.data;
};

export const updateBookingExtraChargeItem = async (
  bookingId,
  itemIndex,
  { amount, note = '', gstPercent = 0 } = {},
) => {
  const safeBookingId = getValidBookingIdOrThrow(bookingId);
  const response = await api.patch(`/booking/api/${safeBookingId}/extra-charges/${itemIndex}/`, {
    extraChargesAmount: amount,
    extraChargesNote: note,
    extraChargesReason: note,
    reason: note,
    extraChargesGstPercent: gstPercent,
  });
  notifyBookingMutation({
    type: 'booking_updated',
    bookingId: safeBookingId,
  });
  return response.data;
};

export const removeBookingExtraChargeItem = async (bookingId, itemIndex) => {
  const safeBookingId = getValidBookingIdOrThrow(bookingId);
  const response = await api.delete(`/booking/api/${safeBookingId}/extra-charges/${itemIndex}/`);
  notifyBookingMutation({
    type: 'booking_updated',
    bookingId: safeBookingId,
  });
  return response.data;
};

/**
 * Get booking for editing (includes additional data like room types, rate plans)
 */
export const getBookingForEdit = async (bookingId) => {
  try {
    const safeBookingId = getValidBookingIdOrThrow(bookingId);
    const response = await api.get(`/booking/api/${safeBookingId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get room type images for a booking (separate API call for lazy loading)
 */
export const getBookingRoomTypeImages = async (bookingId) => {
  try {
    const response = await api.get(`/booking/api/${bookingId}/room-type-images/`);
    return response.data;
  } catch (error) {
    // Error fetching booking room type images:', error);
    throw error;
  }
};

/**
 * Get guest document signed URLs for a booking (separate API call for lazy loading).
 * Uses a short TTL cache + in-flight dedupe for smoother drawer opens.
 */
export const getBookingGuestDocuments = async (bookingId, guestId) => {
  return getOrFetchBookingGuestDocuments(bookingId, guestId, async (bId, gId) => {
    const response = await api.get(`/booking/api/${bId}/guest-documents/`, {
      params: { guest_id: gId },
    });
    return response.data;
  });
};

/**
 * Fetch booking confirmation voucher PDF as Blob (for preview)
 * @param {number} bookingId
 * @param {{ groupVoucher?: boolean }} [options]
 */
export const fetchBookingConfirmationPdfBlob = async (bookingId, options = {}) => {
  const params = {};
  if (options.groupVoucher === true) {
    params.group_voucher = 1;
  }
  const response = await api.get(`/booking/api/${bookingId}/download-confirmation/`, {
    params,
    responseType: 'blob',
  });
  return ensurePdfBlob(response.data, 'Failed to load confirmation PDF');
};

/**
 * Download booking confirmation voucher PDF (triggers browser download)
 * @param {number} bookingId
 * @param {{ groupVoucher?: boolean }} [options] groupVoucher=true merges all group member vouchers
 */
export const downloadBookingConfirmation = async (bookingId, options = {}) => {
  const params = {};
  if (options.groupVoucher === true) {
    params.group_voucher = 1;
  }
  const response = await api.get(`/booking/api/${bookingId}/download-confirmation/`, {
    params,
    responseType: 'blob',
  });
  const blob = response.data;
  const disposition = response.headers['content-disposition'];
  const filename = parseContentDispositionFilename(
    disposition,
    `BookingConfirmationVoucher_${bookingId}.pdf`
  );
  await downloadBlob(blob, filename);
};

/**
 * Fetch booking invoice PDF as Blob (for preview)
 * @param {number} bookingId
 * @param {string} invoiceScope
 * @param {{ groupInvoice?: boolean }} [options]
 */
export const fetchBookingInvoicePdfBlob = async (
  bookingId,
  invoiceScope = 'hotel',
  options = {}
) => {
  const params = { invoice_scope: invoiceScope || 'hotel' };
  if (options.groupInvoice === true) {
    params.group_invoice = 1;
  }
  const response = await api.get(`/booking/api/${bookingId}/download-invoice/`, {
    params,
    responseType: 'blob',
  });
  return ensurePdfBlob(response.data, 'Failed to load invoice PDF');
};

/**
 * Fetch default draft invoice editor values from booking (read-only).
 */
export const fetchDraftInvoiceDefaults = async (bookingId, invoiceScope = 'hotel') => {
  const response = await api.get(`/booking/api/${bookingId}/draft-invoice/defaults/`, {
    params: { invoice_scope: invoiceScope || 'hotel' },
  });
  return response.data;
};

/**
 * Generate draft invoice PDF from custom payload (no DB writes).
 */
export const fetchDraftInvoicePdfBlob = async (bookingId, draftPayload = {}) => {
  const response = await api.post(`/booking/api/${bookingId}/draft-invoice/`, draftPayload, {
    responseType: 'blob',
  });
  return ensurePdfBlob(response.data, 'Failed to load draft invoice PDF');
};

export const fetchFinanceDraftInvoiceDefaults = async (checkIn = null) => {
  const params = {};
  if (checkIn) {
    params.check_in = checkIn;
  }
  const response = await api.get('/finance/api/draft-invoice/defaults/', { params });
  return response.data;
};

export const fetchFinanceDraftInvoicePdfBlob = async (draftPayload = {}) => {
  const response = await api.post('/finance/api/draft-invoice/', draftPayload, {
    responseType: 'blob',
  });
  return ensurePdfBlob(response.data, 'Failed to load draft invoice PDF');
};

/**
 * Download booking invoice PDF (triggers browser download)
 * @param {number} bookingId
 * @param {string} invoiceScope
 * @param {{ groupInvoice?: boolean }} [options] groupInvoice=true for GRP combined invoice only
 */
export const downloadBookingInvoice = async (
  bookingId,
  invoiceScope = 'hotel',
  options = {}
) => {
  const params = { invoice_scope: invoiceScope || 'hotel' };
  if (options.groupInvoice === true) {
    params.group_invoice = 1;
  }
  const response = await api.get(`/booking/api/${bookingId}/download-invoice/`, {
    params,
    responseType: 'blob',
  });
  const blob = response.data;
  const disposition = response.headers['content-disposition'];
  const filename = parseContentDispositionFilename(disposition, `Invoice_${bookingId}.pdf`);
  await downloadBlob(blob, filename);
};

/**
 * Send booking confirmation and invoice to guest via email and/or WhatsApp
 * @param {number} bookingId
 * @param {{ send_email?: boolean, send_whatsapp?: boolean, invoice_scope?: 'hotel'|'restaurant'|'combined' }} options
 * @returns {{ email_sent: boolean, whatsapp_sent: boolean, errors: string[] }}
 */
export const getHotelCommunicationSettings = async () => {
  const response = await api.get('/configuration/api/communication-settings/');
  return response.data;
};

export const updateHotelCommunicationSettings = async (payload) => {
  const response = await api.patch('/configuration/api/communication-settings/', payload);
  return response.data;
};

/** @returns {boolean} whether guest email or WhatsApp is on for booking-created notifications */
export function isBookingCreatedGuestNotifyEnabled(settings) {
  if (!settings) return false;
  return (
    isCommEventEnabled(settings, 'booking_created', 'email') ||
    isCommEventEnabled(settings, 'booking_created', 'whatsapp')
  );
}

/** @returns {boolean} whether guest channel is enabled for an event */
export function isCommEventEnabled(settings, eventKey, channel, audience = 'guest', section = null) {
  if (!settings) return false;
  if (channel === 'whatsapp' && !settings.whatsapp_enabled) return false;
  if (channel === 'email' && !settings.email_enabled) return false;
  const ev = settings.events?.[eventKey];
  if (!ev) return false;
  if (eventKey === 'review_guest' && audience === 'guest') {
    const manual = ev.manual?.guest;
    if (manual && manual[channel] !== undefined) return Boolean(manual[channel]);
    return false;
  }
  if (eventKey === 'hall_event') {
    const secKey = section || 'manual';
    const aud = ev[secKey]?.[audience];
    if (!aud || aud[channel] === undefined) return false;
    return Boolean(aud[channel]);
  }
  const aud = ev[audience];
  if (!aud || aud[channel] === undefined) return false;
  return Boolean(aud[channel]);
}

export function isCommReviewCheckoutEnabled(settings, channel) {
  if (!settings) return false;
  if (channel === 'whatsapp' && !settings.whatsapp_enabled) return false;
  if (channel === 'email' && !settings.email_enabled) return false;
  const guest = settings.events?.review_guest?.checkout_auto?.guest;
  if (!guest || guest[channel] === undefined) return false;
  return Boolean(guest[channel]);
}

export const sendBookingReviewRequest = async (bookingId, options = {}) => {
  const response = await api.post(`/booking/api/${bookingId}/send-review/`, {
    send_email: options.send_email === true,
    send_whatsapp: options.send_whatsapp === true,
  });
  return response.data;
};

export const sendCustomerOfferMessage = async (guestId, options = {}) => {
  const response = await api.post(`/customerhub/api/customers/${guestId}/send-offer/`, {
    message: options.message || '',
    send_email: options.send_email === true,
    send_whatsapp: options.send_whatsapp === true,
  });
  return response.data;
};

export const sendBookingConfirmation = async (bookingId, options = {}) => {
  const body = {
    send_email: options.send_email === true,
    send_whatsapp: options.send_whatsapp === true,
    invoice_scope: options.invoice_scope || 'hotel',
  };
  if (options.recipient_guest_id != null && options.recipient_guest_id !== '') {
    body.recipient_guest_id = Number(options.recipient_guest_id);
  }
  if (options.groupInvoice === true || options.group_invoice === true) {
    body.group_invoice = true;
  }
  if (options.groupVoucher === true || options.group_voucher === true) {
    body.group_voucher = true;
  }
  // Explicit false when caller wants single-member docs despite group membership.
  if (options.groupInvoice === false || options.group_invoice === false) {
    body.group_invoice = false;
  }
  if (options.groupVoucher === false || options.group_voucher === false) {
    body.group_voucher = false;
  }
  const response = await api.post(`/booking/api/${bookingId}/send-confirmation/`, body);
  return response.data;
};

/** Whether a payment-link API response actually delivered on the requested channel(s). */
export function getPaymentLinkDeliveryResult(res, channel) {
  const errs = (res?.errors || []).filter(Boolean);
  if (errs.length) {
    return { ok: false, message: errs.join(' ') };
  }
  if (channel === 'email') {
    return res?.email_sent
      ? { ok: true, message: 'Payment link sent by email.' }
      : { ok: false, message: 'Email was not sent (check SMTP / guest email).' };
  }
  if (channel === 'whatsapp') {
    return res?.whatsapp_sent
      ? { ok: true, message: formatWhatsAppSuccessMessage(res, { prefix: 'Payment link queued on WhatsApp' }) }
      : { ok: false, message: formatWhatsAppErrorMessage(res) };
  }
  if (channel === 'both') {
    if (res?.email_sent && res?.whatsapp_sent) {
      const waPart = formatWhatsAppSuccessMessage(res, { prefix: 'Payment link queued on WhatsApp' });
      return { ok: true, message: `Payment link sent by email. ${waPart}` };
    }
    if (!res?.email_sent && !res?.whatsapp_sent) {
      return { ok: false, message: 'Email and WhatsApp were not sent.' };
    }
    return {
      ok: false,
      message: !res?.email_sent
        ? 'Email was not sent.'
        : 'WhatsApp was not delivered.',
    };
  }
  return { ok: false, message: 'Unable to verify delivery.' };
}

/**
 * Create Razorpay payment link for booking balance and send to guest (email / WhatsApp / both).
 * @param {number} bookingId
 * @param {{ channel: 'email'|'whatsapp'|'both', link_type?: 'standard'|'upi', amount?: number|string }} options
 */
export const sendBookingPaymentLink = async (bookingId, options = {}) => {
  const body = {
    action: 'send',
    channel: options.channel,
    link_type: options.link_type || 'standard',
  };
  if (options.amount != null && options.amount !== '') {
    let n;
    if (typeof options.amount === 'number') {
      n = options.amount;
    } else {
      let s = String(options.amount)
        .trim()
        .replace(/,/g, '')
        .replace(/\u00a0/g, ' ')
        .replace(/\u20b9/g, '')
        .replace(/[$€£]/g, '')
        .trim();
      s = s.replace(/^(INR|USD|EUR|GBP)\s*/i, '').trim();
      n = parseFloat(s);
    }
    if (!Number.isNaN(n)) {
      body.amount = n;
    }
  }
  const response = await api.post(`/booking/api/${bookingId}/payment-link/`, body);
  return response.data;
};

/**
 * Poll Razorpay for payment link status and record payment if paid (idempotent).
 * @param {number} bookingId
 * @param {number} linkId — local BookingRazorpayPaymentLink id
 */
export const syncBookingPaymentLink = async (bookingId, linkId) => {
  const response = await api.post(`/booking/api/${bookingId}/payment-link/`, {
    action: 'sync',
    link_id: linkId,
  });
  return response.data;
};

/**
 * Update booking status
 */
export const updateBookingStatus = async (bookingId, status) => {
  try {
    const response = await api.patch(`/booking/api/${bookingId}/`, { status });
    notifyBookingMutation({ type: 'booking_status', bookingId, status });
    return response.data;
  } catch (error) {
    // Error updating booking status:', error);
    throw error;
  }
};

/**
 * Mark booking as no-show
 */
export const markBookingNoShow = async (bookingId, reason = '') => {
  try {
    const response = await api.post(`/booking/api/${bookingId}/mark-no-show/`, { reason });
    notifyBookingMutation({ type: 'no_show', bookingId });
    return response.data;
  } catch (error) {
    // Error marking booking as no-show:', error);
    throw error;
  }
};

/**
 * Update stay-guest (booking snapshot) details for a booking.
 * Pass guest.id plus profile fields from the stay guest drawer.
 */
export const updateBookingStayGuest = async (bookingId, guestPayload) => {
  const safeBookingId = getValidBookingIdOrThrow(bookingId);
  const response = await api.patch(`/booking/api/${safeBookingId}/`, {
    guest: guestPayload,
  });
  notifyBookingMutation({ type: 'stay_guest', bookingId: safeBookingId });
  return response.data;
};

/**
 * Update booking checkout date (extend stay)
 */
export const updateBookingCheckoutDate = async (bookingId, checkOutDate) => {
  try {
    const response = await api.patch(`/booking/api/${bookingId}/`, { 
      check_out_date: checkOutDate 
    });
    notifyBookingMutation({ type: 'checkout_date', bookingId });
    return response.data;
  } catch (error) {
    // Error updating booking checkout date:', error);
    throw error;
  }
};

/**
 * Get booking statistics for dashboard
 */
export const getBookingStatistics = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await api.get('/dashboard/api/booking-statistics/', { params });
    return response.data;
  } catch (error) {
    // Error fetching booking statistics:', error);
    throw error;
  }
};

/**
 * Get organization-level dashboard statistics
 * @param {number|number[]|null} hotelIds - Single hotel ID, array of hotel IDs, or null for all hotels
 * @param {string|null} startDate - Start date in YYYY-MM-DD format
 * @param {string|null} endDate - End date in YYYY-MM-DD format
 */
export const getOrganizationStatistics = async (hotelIds = null, startDate = null, endDate = null) => {
  try {
    const params = {};
    // Handle multiple hotel IDs - send as array or multiple parameters
    if (hotelIds) {
      if (Array.isArray(hotelIds) && hotelIds.length > 0) {
        // For arrays, we need to send multiple hotel_id parameters
        // Axios will serialize arrays as hotel_id[]=1&hotel_id[]=2 by default
        // But Django's getlist() expects hotel_id=1&hotel_id=2
        // So we'll use paramsSerializer or build the URL manually
        // Actually, axios with default paramsSerializer will create hotel_id[] format
        // We need to use a custom serializer or handle it in the config
        params.hotel_id = hotelIds;
      } else if (!Array.isArray(hotelIds)) {
        // Single hotel ID (backward compatibility)
        params.hotel_id = hotelIds;
      }
    }
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Use custom params serializer to handle arrays correctly for Django
    // Django's getlist() expects: hotel_id=1&hotel_id=2 (not hotel_id[]=1&hotel_id[]=2)
    const config = {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            // For arrays, add each value with the same key
            value.forEach((item) => {
              searchParams.append(key, item);
            });
          } else if (value !== null && value !== undefined) {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      },
    };
    
    const response = await api.get('/dashboard/api/organization-statistics/', config);
    const payload = response.data || {};
    const stats = payload.statistics || {};
    // API returns daily_stats at root; charts read statistics.daily_stats — merge so consumers stay simple.
    return {
      ...payload,
      statistics: {
        ...stats,
        daily_stats: payload.daily_stats ?? stats.daily_stats ?? [],
      },
    };
  } catch (error) {
    // Enhanced error logging
    const errorInfo = {
      message: error.userMessage || error.message,
      status: error.statusCode || error.response?.status,
      url: '/dashboard/api/organization-statistics/',
      params: { hotelIds, startDate, endDate },
      responseData: error.responseData,
    };
    
    // Error fetching organization statistics:', errorInfo);
    
    // Throw enhanced error with user-friendly message
    const enhancedError = new Error(error.userMessage || 'Failed to load organization statistics');
    enhancedError.statusCode = error.statusCode || error.response?.status;
    enhancedError.originalError = error;
    enhancedError.responseData = error.responseData;
    throw enhancedError;
  }
};

/**
 * Get rates data for calendar view
 * Uses DRF API endpoint which returns JSON by default
 */
const inFlightRatesRequests = new Map();

export const getRatesData = async (startDate, endDate, channelId = 'pms', options = {}) => {
  const normalizedChannel = channelId || 'pms';
  const roomTypeId = options.roomTypeId ?? options.room_type_id ?? null;
  const includeRatePlans = options.includeRatePlans !== false && options.include_rate_plans !== false;
  const requestKey = `${startDate}|${endDate}|${normalizedChannel}|${roomTypeId || ''}|${includeRatePlans ? 1 : 0}`;

  if (inFlightRatesRequests.has(requestKey)) {
    return inFlightRatesRequests.get(requestKey);
  }

  const requestPromise = (async () => {
    try {
      const params = {
          action: 'list',
          start_date: startDate,
          end_date: endDate,
      };
      
      if (normalizedChannel === 'all') {
        params.all_channels = 1;
      } else if (normalizedChannel !== 'pms') {
        params.channel_id = normalizedChannel;
      }
      if (roomTypeId) {
        params.room_type_id = roomTypeId;
      }
      if (!includeRatePlans) {
        params.include_rate_plans = 0;
      }
      
      const response = await api.get('/finance/api/rates/', { params });
      return response.data;
    } catch (error) {
      // Error fetching rates data:', error);
      throw error;
    } finally {
      inFlightRatesRequests.delete(requestKey);
    }
  })();

  inFlightRatesRequests.set(requestKey, requestPromise);
  return requestPromise;
};

/**
 * Get rate for specific date and room type
 * Uses DRF API endpoint which returns JSON by default
 */
export const getRateForDate = async (date, roomTypeId, ratePlanId, channelId = 'pms') => {
  try {
    const params = {
        action: 'get_rate',
        date: date,
        room_type_id: roomTypeId,
        rate_plan_id: ratePlanId,
    };
    
    // Add channel parameter if not PMS
    if (channelId && channelId !== 'pms') {
      params.channel_id = channelId;
    }
    
    const response = await api.get('/finance/api/rates/', { params });
    return response.data;
  } catch (error) {
    // Error fetching rate:', error);
    throw error;
  }
};

/**
 * Save or update rate
 * Uses DRF API endpoint which handles POST requests directly
 */
export const saveRate = async (rateData) => {
  try {
    const payload = {
      ...rateData,
    };
    
    // Include channel_id if provided (even if 'pms')
    if (rateData.channel_id) {
      payload.channel_id = rateData.channel_id;
    }
    
    const response = await api.post('/finance/api/rates/', payload);
    return response.data;
  } catch (error) {
    // Error saving rate:', error);
    throw error;
  }
};

/**
 * Bulk save rates for multiple dates
 * Uses bulk update endpoint to update multiple dates in a single API call
 */
export const bulkSaveRates = async (updates, options = {}) => {
  try {
    const mapRateFields = (row) => ({
      room_type_id: row.room_type_id,
      rate_plan_id: row.rate_plan_id || null,
      channel_id: row.channel_id || null,
      base_rate: Number(row.base_rate),
      single_occupancy_rate:
        row.single_occupancy_rate != null && row.single_occupancy_rate !== ''
          ? Number(row.single_occupancy_rate)
          : null,
      extra_adult_rate:
        row.extra_adult_rate != null && row.extra_adult_rate !== ''
          ? Number(row.extra_adult_rate)
          : 0,
      extra_child_rate:
        row.extra_child_rate != null && row.extra_child_rate !== ''
          ? Number(row.extra_child_rate)
          : 0,
      min_los: row.min_los || null,
      max_los: row.max_los || null,
      available: row.available !== undefined ? row.available : true,
      currency: row.currency || 'INR',
    });
    const payload = {
      persist_after_push: Boolean(options.persistAfterPush),
    };
    if (options.compact) {
      payload.dates = options.compact.dates;
      payload.weekday_factor = options.compact.weekdayFactor ?? 1;
      payload.weekend_factor = options.compact.weekendFactor ?? 1;
      payload.items = (options.compact.items || []).map(mapRateFields);
    } else {
      payload.updates = (updates || []).map((update) => ({
        date: update.date,
        ...mapRateFields(update),
      }));
    }

    const response = await api.post('/finance/api/rates/?action=bulk', payload);
    return response.data;
  } catch (error) {
    // Error bulk saving rates:', error);
    throw error;
  }
};

/**
 * Apply/clear rate-plan restrictions via Aiosell rate-restrictions-push.
 * items: [{ room_type_id, rate_plan_id }, ...]
 * Never sets stopSell — use setRateStopSell for that.
 */
export const setRateRestrictionsRange = async ({
  startDate,
  endDate,
  dates = null,
  items,
  apply = true,
  restrictions = {},
  channelIds = null,
} = {}) => {
  const payload = {
    start_date: startDate,
    end_date: endDate,
    items,
    apply: !!apply,
    restrictions,
  };
  if (Array.isArray(dates) && dates.length) payload.dates = dates;
  if (Array.isArray(channelIds) && channelIds.length) {
    payload.channel_ids = channelIds.filter(
      (id) => id != null && id !== '' && id !== 'pms' && id !== 'all'
    );
  }
  const response = await api.post('/finance/api/rates/?action=set_rate_restrictions', payload);
  return response.data;
};

/**
 * Apply/clear stop-sell on rate plans via Aiosell rate-restrictions-push.
 */
export const setRateStopSell = async ({
  startDate,
  endDate,
  dates = null,
  items,
  stopSell = true,
  channelIds = null,
  platform = null,
} = {}) => {
  const payload = {
    start_date: startDate,
    end_date: endDate,
    items,
    stop_sell: !!stopSell,
  };
  if (Array.isArray(dates) && dates.length) payload.dates = dates;
  if (platform) payload.platform = platform;
  if (Array.isArray(channelIds) && channelIds.length) {
    payload.channel_ids = channelIds.filter(
      (id) => id != null && id !== '' && id !== 'pms' && id !== 'all'
    );
  }
  const response = await api.post('/finance/api/rates/?action=set_rate_stop_sell', payload);
  return response.data;
};

/**
 * Sync stored rates to Aiosell via one update-rates (rate-push) call.
 * Different nightly rates become separate updates[] blocks in that single call.
 * Ref: https://apidocs.aiosell.com/api/rate-push
 */
export const syncRates = async ({
  startDate,
  endDate,
  dates = null,
  items,
} = {}) => {
  const payload = {
    start_date: startDate,
    end_date: endDate,
    items,
  };
  if (Array.isArray(dates) && dates.length) payload.dates = dates;
  const response = await api.post('/finance/api/rates/?action=sync_rates', payload);
  return response.data;
};

export const getChannelMappings = async () => {
  const response = await api.get('/distribution/api/channel-mappings/');
  return response.data?.mappings || [];
};

/**
 * Get all channels
 * Note: Now uses distribution API endpoint which provides full CRUD
 * Filters to return only enabled channels to match previous behavior
 */
export const getChannels = async (config = {}) => {
  try {
    const response = await api.get('/distribution/api/channels/', config);
    if (response.data?.channels) {
      const enabledChannels = response.data.channels.filter(ch => ch.enabled);
      return { channels: enabledChannels };
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get rate plans (meal plans) for the selected channel
 */
export const getRatePlans = async (channelId = null) => {
  try {
    const params = {
      json: 1,
    };
    
    if (channelId && channelId !== 'pms') {
      params.channel_id = channelId;
    }
    
    const response = await api.get('/configuration/api/rate-plans/', { params });
    return response.data;
  } catch (error) {
    // Error fetching rate plans:', error);
    throw error;
  }
};

/**
 * Get all rate plans for configuration page
 */
export const getConfigurationRatePlans = async (params = {}) => {
  try {
    const response = await api.get('/configuration/api/rate-plans/', { params });
    return response.data.rate_plans || [];
  } catch (error) {
    // Error fetching configuration rate plans:', error);
    throw error;
  }
};

/**
 * Create a new rate plan
 */
export const createRatePlan = async (data) => {
  try {
    const response = await api.post('/configuration/api/rate-plans/', data);
    return response.data;
  } catch (error) {
    // Error creating rate plan:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update an existing rate plan
 */
export const updateRatePlan = async (ratePlanId, data) => {
  try {
    const response = await api.put(`/configuration/api/rate-plans/${ratePlanId}/`, data);
    return response.data;
  } catch (error) {
    // Error updating rate plan:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete a rate plan
 */
export const deleteRatePlan = async (ratePlanId) => {
  try {
    const response = await api.delete('/configuration/api/rate-plans/', {
      params: { id: ratePlanId }
    });
    return response.data;
  } catch (error) {
    // Error deleting rate plan:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

export const reorderRatePlans = async (ids) => {
  const response = await api.post('/configuration/api/rate-plans/reorder/', { ids });
  return response.data?.rate_plans || [];
};

/**
 * Get all room types for the selected hotel
 */
export const getConfigurationRoomTypes = async (options = {}) => {
  try {
    const params = {};
    if (options.includeInactive) {
      params.include_inactive = 1;
    }
    const response = await api.get('/configuration/api/room-types/', { params });
    return response.data.room_types || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new room type
 */
export const createRoomType = async (roomTypeData) => {
  try {
    const response = await api.post('/configuration/api/room-types/', roomTypeData);
    return response.data;
  } catch (error) {
    // Error creating room type:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update an existing room type
 */
export const updateRoomType = async (roomTypeId, roomTypeData) => {
  try {
    const response = await api.put('/configuration/api/room-types/', { ...roomTypeData, id: roomTypeId });
    return response.data;
  } catch (error) {
    // Error updating room type:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Enable or disable a room type
 */
export const toggleRoomTypeStatus = async (roomTypeId, isActive) => {
  try {
    const response = await api.patch('/configuration/api/room-types/', {
      id: roomTypeId,
      is_active: isActive,
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete a room type
 */
export const deleteRoomType = async (roomTypeId) => {
  try {
    const response = await api.delete('/configuration/api/room-types/', { params: { id: roomTypeId } });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get all floors for the selected hotel
 */
export const getConfigurationFloors = async (options = {}) => {
  try {
    const params = {};
    if (options.includeInactive) {
      params.include_inactive = 1;
    }
    const response = await api.get('/configuration/api/floors/', { params });
    return response.data.floors || [];
  } catch (error) {
    // Error fetching floors:', error);
    throw error;
  }
};

/**
 * Create a new floor
 */
export const createFloor = async (floorData) => {
  try {
    const response = await api.post('/configuration/api/floors/', floorData);
    return response.data;
  } catch (error) {
    // Error creating floor:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update an existing floor
 */
export const updateFloor = async (floorId, floorData) => {
  try {
    const response = await api.put('/configuration/api/floors/', { ...floorData, id: floorId });
    return response.data;
  } catch (error) {
    // Error updating floor:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete a floor
 */
export const deleteFloor = async (floorId) => {
  try {
    const response = await api.delete('/configuration/api/floors/', { params: { id: floorId } });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get all rooms grouped by floor for the selected hotel
 */
export const getConfigurationRooms = async () => {
  try {
    const response = await api.get('/configuration/api/rooms/');
    return response.data.rooms_by_floor || [];
  } catch (error) {
    // Error fetching rooms:', error);
    throw error;
  }
};

/**
 * Create a new room
 */
export const createRoom = async (roomData) => {
  try {
    const response = await api.post('/configuration/api/rooms/', roomData);
    return response.data;
  } catch (error) {
    // Error creating room:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update an existing room
 */
export const updateRoom = async (roomId, roomData) => {
  try {
    const response = await api.put('/configuration/api/rooms/', { ...roomData, id: roomId });
    return response.data;
  } catch (error) {
    // Error updating room:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete a room
 */
export const deleteRoom = async (roomId) => {
  try {
    const response = await api.delete('/configuration/api/rooms/', { params: { id: roomId } });
    return response.data;
  } catch (error) {
    // Error deleting room:', error);
    throw error;
  }
};

/**
 * Search guest by phone/mobile number
 */
export const searchGuestByPhone = async (phone) => {
  try {
    // Extract only the phone number (digits) without country code
    // Remove all non-digit characters to get just the number
    const phoneNumberOnly = phone ? phone.replace(/\D/g, '') : '';
    
    const response = await api.get('/customerhub/api/search-guest/', {
      params: {
        phone: phoneNumberOnly,
      },
    });
    return response.data;
  } catch (error) {
    // Error searching guest:', error);
    throw error;
  }
};

/**
 * Get guest statistics for dashboard
 */
export const getGuestStatistics = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/customerhub/api/statistics/', { params });
    return response.data;
  } catch (error) {
    // Error fetching guest statistics:', error);
    throw error;
  }
};

/**
 * Get monthly guest growth data
 */
export const getGuestMonthlyGrowth = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/customerhub/api/monthly-growth/', { params });
    return response.data;
  } catch (error) {
    // Error fetching monthly growth:', error);
    throw error;
  }
};

/**
 * Get top 5 customers by bookings and amount
 */
export const getTopCustomers = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/customerhub/api/top-customers/', { params });
    return response.data;
  } catch (error) {
    // Error fetching top customers:', error);
    throw error;
  }
};

/**
 * Get guest location distribution (by country, state, or city)
 */
export const getGuestLocationDistribution = async (groupBy = 'country', startDate = null, endDate = null) => {
  try {
    const params = { group_by: groupBy };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/customerhub/api/location-distribution/', {
      params,
    });
    return response.data;
  } catch (error) {
    // Error fetching location distribution:', error);
    throw error;
  }
};

/**
 * Get guest age group distribution
 */
export const getGuestAgeGroupDistribution = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/customerhub/api/age-group-distribution/', { params });
    return response.data;
  } catch (error) {
    // Error fetching age group distribution:', error);
    throw error;
  }
};

/**
 * Get repeated guest distribution by age group for the selected period
 */
export const getRepeatedGuestAgeGroupDistribution = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/customerhub/api/repeated-guest-age-distribution/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get customer detail with all bookings
 */
export const getCustomerDetail = async (guestId, page = 1, perPage = 10) => {
  try {
    const response = await api.get(`/customerhub/api/customers/${guestId}/`, {
      params: { page, per_page: perPage },
    });
    return response.data;
  } catch (error) {
    // Error fetching customer detail:', error);
    throw error;
  }
};

/**
 * Create a new customer/guest (POST). Returns created guest data.
 */
export const createCustomer = async (payload) => {
  try {
    const response = await api.post('/customerhub/api/customers/', payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update customer/guest details (PATCH). Returns updated customer detail shape.
 */
export const updateCustomer = async (guestId, payload) => {
  try {
    const response = await api.patch(`/customerhub/api/customers/${guestId}/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get inventory dashboard statistics
 */
export const getInventoryDashboardStatistics = async (startDate, endDate, month = null) => {
  try {
    const params = {};
    if (month) {
      // Use month parameter for Monthly Inventory Trends
      params.month = month; // Format: YYYY-MM
    } else if (startDate && endDate) {
      // Use date range for other statistics
      params.start_date = startDate; // Format: YYYY-MM-DD
      params.end_date = endDate; // Format: YYYY-MM-DD
    }
    
    const response = await api.get('/inventory/api/dashboard-statistics/', {
      params,
    });
    return response.data;
  } catch (error) {
    // Error fetching inventory dashboard statistics:', error);
    throw error;
  }
};

export const getStockSuppliers = async () => {
  try {
    const response = await api.get('/stock/api/stock-suppliers/', {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStockRawMaterials = async () => {
  try {
    const response = await api.get('/stock/api/stock-raw-materials/', {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createStockRawMaterial = async (payload) => {
  try {
    const response = await api.post('/stock/api/stock-raw-materials/', payload, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateStockRawMaterial = async (rawMaterialId, payload) => {
  try {
    const response = await api.put(`/stock/api/stock-raw-materials/${rawMaterialId}/`, payload, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteStockRawMaterial = async (rawMaterialId) => {
  try {
    const response = await api.delete(`/stock/api/stock-raw-materials/${rawMaterialId}/`, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createStockSupplier = async (payload) => {
  try {
    const response = await api.post('/stock/api/stock-suppliers/', payload, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateStockSupplier = async (supplierId, payload) => {
  try {
    const response = await api.put(`/stock/api/stock-suppliers/${supplierId}/`, payload, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteStockSupplier = async (supplierId) => {
  try {
    const response = await api.delete(`/stock/api/stock-suppliers/${supplierId}/`, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStockInventories = async (params = {}) => {
  try {
    const response = await api.get('/stock/api/stock-inventories/', {
      params,
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStockDashboardStats = async ({ startDate, endDate, month }) => {
  try {
    const response = await api.get('/stock/api/dashboard-stats/', {
      params: {
        start_date: startDate,
        end_date: endDate,
        month,
      },
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStockInventoryById = async (inventoryId) => {
  try {
    const response = await api.get(`/stock/api/stock-inventories/${inventoryId}/`, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createStockInventory = async (payload) => {
  try {
    const response = await api.post('/stock/api/stock-inventories/', payload, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateStockInventory = async (inventoryId, payload) => {
  try {
    const response = await api.put(`/stock/api/stock-inventories/${inventoryId}/`, payload, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteStockInventory = async (inventoryId) => {
  try {
    const response = await api.delete(`/stock/api/stock-inventories/${inventoryId}/`, {
      headers: getBearerTokenHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStockCurrentStock = async (params = {}) => {
  const response = await api.get('/stock/api/current-stock/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockItemAvailability = async (itemId, params = {}) => {
  const response = await api.get(`/stock/api/items/${itemId}/availability/`, {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockItemActivity = async (itemId, params = {}) => {
  const response = await api.get(`/stock/api/items/${itemId}/activity/`, {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockOutWorksheet = async (params = {}) => {
  const response = await api.get('/stock/api/stock-out/worksheet/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockOutRecords = async (params = {}) => {
  const response = await api.get('/stock/api/stock-out/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockOutRecord = async (recordId) => {
  const response = await api.get(`/stock/api/stock-out/${recordId}/`, {
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const createStockOutRecord = async (payload) => {
  const response = await api.post('/stock/api/stock-out/', payload, {
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const updateStockOutRecord = async (recordId, payload) => {
  const response = await api.put(`/stock/api/stock-out/${recordId}/`, payload, {
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const deleteStockOutRecord = async (recordId) => {
  const response = await api.delete(`/stock/api/stock-out/${recordId}/`, {
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockLedger = async (params = {}) => {
  const response = await api.get('/stock/api/stock-ledger/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockAdjustments = async (params = {}) => {
  const response = await api.get('/stock/api/stock-adjustments/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const createStockAdjustment = async (payload) => {
  const response = await api.post('/stock/api/stock-adjustments/', payload, {
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockTransfers = async (params = {}) => {
  const response = await api.get('/stock/api/stock-transfers/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const createStockTransfer = async (payload) => {
  const response = await api.post('/stock/api/stock-transfers/', payload, {
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockInventoryMetrics = async ({ startDate, endDate } = {}) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/stock/api/inventory-metrics/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockReport = async (params = {}) => {
  const response = await api.get('/stock/api/stock-reports/', {
    params,
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const getStockCategories = async () => {
  const response = await api.get('/stock/api/stock-categories/', {
    headers: getBearerTokenHeader(),
  });
  return response.data;
};

export const extractStockInvoiceData = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/stock/api/invoice-extract/', formData, {
      headers: {
        ...getBearerTokenHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all rooms grouped by room type for walk-in booking
 */
export const getAllRooms = async (params = {}) => {
  try {
    const response = await api.get('/booking/api/all-rooms/', { params });
    return response.data;
  } catch (error) {
    // Error fetching all rooms:', error);
    throw error;
  }
};

/**
 * Get list of hotels for the current user
 */
export const getHotels = async () => {
  try {
    const response = await api.get('/onboarding/landing/', {
      params: { json: 1 },
    });
    return response.data.hotels || [];
  } catch (error) {
    // Error fetching hotels:', error);
    throw error;
  }
};

/**
 * Switch selected hotel
 */
export const switchHotel = async (hotelId) => {
  try {
    // Hotel switching is done via URL parameter which updates session
    // We'll use a simple redirect approach
    window.location.href = `${window.location.pathname}?hotel_id=${hotelId}`;
  } catch (error) {
    // Error switching hotel:', error);
    throw error;
  }
};

/**
 * Get finance dashboard statistics
 */
export const getFinanceDashboardStatistics = async (startDate, endDate) => {
  try {
    const response = await api.get('/finance/api/dashboard-statistics/', {
      params: {
        start_date: startDate, // Format: YYYY-MM-DD
        end_date: endDate, // Format: YYYY-MM-DD
      },
    });
    return response.data;
  } catch (error) {
    // Error fetching finance dashboard statistics:', error);
    throw error;
  }
};

/**
 * Get hotel basic details
 */
export const getHotelBasicDetails = async () => {
  try {
    const response = await api.get('/configuration/api/basic-details/');
    return response.data;
  } catch (error) {
    // Error fetching hotel basic details:', error);
    throw error;
  }
};

/**
 * Save hotel basic details
 */
export const saveHotelBasicDetails = async (data) => {
  try {
    const response = await api.post('/configuration/api/basic-details/', data);
    return response.data;
  } catch (error) {
    // Error saving hotel basic details:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * File Manager API
 */

/**
 * Get files list
 */
export const getFiles = async (category = 'photos', type = 'property') => {
  try {
    const response = await api.get('/configuration/api/files/', {
      params: { category, type }
    });
    return response.data;
  } catch (error) {
    // Error fetching files:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Upload a file
 */
export const uploadFile = async (file, category = 'photos', type = 'property', metadata = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (category === 'documents') {
      formData.append('document_type', metadata.document_type || 'other');
      if (metadata.expiry_date) formData.append('expiry_date', metadata.expiry_date);
    } else {
      formData.append('type', type);
      if (metadata.image_type) formData.append('image_type', metadata.image_type);
      if (metadata.room_type_id) formData.append('room_type_id', metadata.room_type_id);
      if (metadata.is_primary !== undefined) formData.append('is_primary', metadata.is_primary ? 'true' : 'false');
      if (metadata.alt_text) formData.append('alt_text', metadata.alt_text);
      if (metadata.sort_order !== undefined) formData.append('sort_order', metadata.sort_order);
      if (metadata.tags && Array.isArray(metadata.tags) && metadata.tags.length > 0) {
        formData.append('tags', JSON.stringify(metadata.tags));
      }
    }
    if (metadata.title) formData.append('title', metadata.title);
    if (metadata.description) formData.append('description', metadata.description);

    const response = await api.post('/configuration/api/files/', formData, {
      headers: {
        ...getBearerTokenHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    // Error uploading file:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Upload stock purchase invoice PDF/image into hotel invoices/stock/{inv id or draft}/ folder.
 */
export const uploadStockInvoiceDocument = async (
  file,
  { stockInventoryId = null, draftId = null } = {}
) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (stockInventoryId != null && stockInventoryId !== '') {
      formData.append('stock_inventory_id', String(stockInventoryId));
    } else if (draftId) {
      formData.append('draft_id', draftId);
    }
    const response = await api.post('/stock/api/stock-invoice-upload/', formData, {
      headers: {
        ...getBearerTokenHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete a file
 */
export const deleteFile = async (fileId, category = 'photos', type = 'property') => {
  try {
    const response = await api.delete('/configuration/api/files/', {
      params: { id: fileId, category, type }
    });
    return response.data;
  } catch (error) {
    // Error deleting file:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update file tags
 */
export const updateFileTags = async (fileId, tags, category = 'photos', type = 'property') => {
  try {
    const response = await api.patch('/configuration/api/files/', {
      id: fileId,
      tags,
      category,
      type
    });
    return response.data;
  } catch (error) {
    // Error updating file tags:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Invoice setup — templates and hotel selection
 */
export const getInvoiceSetup = async () => {
  const response = await api.get('/configuration/api/invoice-setup/');
  return response.data;
};

export const updateInvoiceSetup = async (payload, files = {}) => {
  const hasFiles = Boolean(files.invoice_logo || files.digital_signature);
  const hasRemovals = Boolean(payload.remove_invoice_logo || payload.remove_digital_signature);

  // Always use multipart when files/removals are involved. Do NOT set Content-Type
  // manually — the browser must add the multipart boundary or Django won't parse fields.
  if (hasFiles || hasRemovals) {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      formData.append(key, typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value));
    });
    if (files.invoice_logo) formData.append('invoice_logo', files.invoice_logo);
    if (files.digital_signature) formData.append('digital_signature', files.digital_signature);
    const response = await api.put('/configuration/api/invoice-setup/', formData, {
      headers: {
        ...getBearerTokenHeader(),
      },
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        },
      ],
    });
    return response.data;
  }

  const response = await api.put('/configuration/api/invoice-setup/', payload, {
    headers: {
      ...getBearerTokenHeader(),
    },
  });
  return response.data;
};

export const createInvoiceTemplate = async (payload) => {
  const response = await api.post('/configuration/api/invoice-templates/', payload);
  return response.data;
};

export const deleteInvoiceTemplate = async (templateId) => {
  const response = await api.delete(`/configuration/api/invoice-templates/${templateId}/`);
  return response.data;
};

export const fetchInvoiceTemplatePreviewBlob = async (templateId) => {
  const response = await api.get(`/configuration/api/invoice-templates/${templateId}/preview/`, {
    responseType: 'blob',
  });
  return ensurePdfBlob(response.data, 'Failed to load invoice template preview');
};

/**
 * Get user profile
 */
export const getUserProfile = async () => {
  try {
    const response = await api.get('/onboarding/api/profile/');
    return response.data;
  } catch (error) {
    // Error fetching user profile:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/onboarding/api/profile/', profileData);
    return response.data;
  } catch (error) {
    // Error updating user profile:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Face login enrollment (Google Cloud Vision)
 */
export const getFaceLoginStatus = async () => {
  try {
    const response = await api.get('/onboarding/api/profile/face/');
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

export const enrollFaceLogin = async (imageBlob) => {
  try {
    const formData = new FormData();
    formData.append('face_image', imageBlob, 'face.jpg');
    const response = await api.post('/onboarding/api/profile/face/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

export const updateFaceLoginEnabled = async (enabled) => {
  try {
    const response = await api.patch('/onboarding/api/profile/face/', { enabled });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

export const removeFaceEnrollment = async () => {
  try {
    await api.delete('/onboarding/api/profile/face/');
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get predefined tags
 */
export const getPredefinedTags = async () => {
  try {
    const response = await api.get('/configuration/api/files/', {
      params: { action: 'tags' }
    });
    return response.data;
  } catch (error) {
    // Error fetching predefined tags:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

// ==================== Housekeeping API Functions ====================

/**
 * Get housekeeping staff list
 */
export const getHousekeepingStaff = async (params = {}) => {
  try {
    const response = await api.get('/housekeeping/api/staff/', { params });
    return response.data;
  } catch (error) {
    // Error fetching housekeeping staff:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Create housekeeping staff
 */
export const createHousekeepingStaff = async (staffData) => {
  try {
    const response = await api.post('/housekeeping/api/staff/', staffData);
    return response.data;
  } catch (error) {
    // Error creating housekeeping staff:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update housekeeping staff
 */
export const updateHousekeepingStaff = async (staffId, staffData) => {
  try {
    const response = await api.put(`/housekeeping/api/staff/${staffId}/`, staffData);
    return response.data;
  } catch (error) {
    // Error updating housekeeping staff:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete housekeeping staff
 */
export const deleteHousekeepingStaff = async (staffId) => {
  try {
    const response = await api.delete(`/housekeeping/api/staff/${staffId}/`);
    return response.data;
  } catch (error) {
    // Error deleting housekeeping staff:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get housekeeping settings for the selected hotel
 */
export const getHousekeepingSettings = async () => {
  try {
    const response = await api.get('/housekeeping/api/settings/');
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update housekeeping settings for the selected hotel
 */
export const updateHousekeepingSettings = async (settingsData) => {
  try {
    const response = await api.put('/housekeeping/api/settings/', settingsData);
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get room cleanings list
 */
export const getRoomCleanings = async (params = {}) => {
  try {
    const response = await api.get('/housekeeping/api/cleanings/', { params });
    return response.data;
  } catch (error) {
    // Error fetching room cleanings:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get a single room cleaning record
 */
export const getRoomCleaning = async (cleaningId) => {
  try {
    const response = await api.get(`/housekeeping/api/cleanings/${cleaningId}/`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Create room cleaning
 */
export const createRoomCleaning = async (cleaningData) => {
  try {
    const response = await api.post('/housekeeping/api/cleanings/', cleaningData);
    return response.data;
  } catch (error) {
    // Error creating room cleaning:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update room cleaning
 */
export const updateRoomCleaning = async (cleaningId, cleaningData) => {
  try {
    const response = await api.put(`/housekeeping/api/cleanings/${cleaningId}/`, cleaningData);
    return response.data;
  } catch (error) {
    // Error updating room cleaning:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Complete room cleaning (fast path — minimal payload, no full serializer round-trip)
 */
export const completeRoomCleaning = async (cleaningId, payload) => {
  try {
    const response = await api.post(`/housekeeping/api/cleanings/${cleaningId}/complete/`, payload);
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete room cleaning
 */
export const deleteRoomCleaning = async (cleaningId) => {
  try {
    const response = await api.delete(`/housekeeping/api/cleanings/${cleaningId}/`);
    return response.data;
  } catch (error) {
    // Error deleting room cleaning:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get maintenance requests list
 */
export const getMaintenanceRequests = async (params = {}) => {
  try {
    const response = await api.get('/housekeeping/api/maintenance/', { params });
    return response.data;
  } catch (error) {
    // Error fetching maintenance requests:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Create maintenance request
 */
export const createMaintenanceRequest = async (requestData) => {
  try {
    const response = await api.post('/housekeeping/api/maintenance/', requestData);
    // Invalidate dashboard calendar so room cards pick up maintenance / block state
    // on the next route change (not only after a full page refresh).
    notifyBookingMutation({
      type: 'maintenance',
      action: 'create',
      roomId: requestData?.room,
      blockRoom: Boolean(requestData?.block_room),
    });
    return response.data;
  } catch (error) {
    // Error creating maintenance request:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update maintenance request
 */
export const updateMaintenanceRequest = async (requestId, requestData) => {
  try {
    const response = await api.put(`/housekeeping/api/maintenance/${requestId}/`, requestData);
    notifyBookingMutation({
      type: 'maintenance',
      action: 'update',
      requestId,
      roomId: requestData?.room,
      blockRoom: Boolean(requestData?.block_room),
    });
    return response.data;
  } catch (error) {
    // Error updating maintenance request:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete maintenance request
 */
export const deleteMaintenanceRequest = async (requestId) => {
  try {
    const response = await api.delete(`/housekeeping/api/maintenance/${requestId}/`);
    notifyBookingMutation({
      type: 'maintenance',
      action: 'delete',
      requestId,
    });
    return response.data;
  } catch (error) {
    // Error deleting maintenance request:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get lost and found items list
 */
export const getLostAndFound = async (params = {}) => {
  try {
    const response = await api.get('/housekeeping/api/lost-and-found/', { params });
    return response.data;
  } catch (error) {
    // Error fetching lost and found items:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Create lost and found item
 */
export const createLostAndFound = async (itemData) => {
  try {
    const response = await api.post('/housekeeping/api/lost-and-found/', itemData);
    return response.data;
  } catch (error) {
    // Error creating lost and found item:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update lost and found item
 */
export const updateLostAndFound = async (itemId, itemData) => {
  try {
    const response = await api.put(`/housekeeping/api/lost-and-found/${itemId}/`, itemData);
    return response.data;
  } catch (error) {
    // Error updating lost and found item:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Delete lost and found item
 */
export const deleteLostAndFound = async (itemId) => {
  try {
    const response = await api.delete(`/housekeeping/api/lost-and-found/${itemId}/`);
    return response.data;
  } catch (error) {
    // Error deleting lost and found item:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

// ==================== Dynamic Pricing API ====================

export const getPricingRecommendations = async (params, config = {}) => {
  const response = await api.get('/dynamic-pricing/api/pricing/recommendations/', { params, ...config });
  return response.data;
};

export const applyPricingRates = async (payload) => {
  const response = await api.post('/dynamic-pricing/api/pricing/apply/', payload);
  return response.data;
};

export const getPricingConfig = async () => {
  const response = await api.get('/dynamic-pricing/api/pricing/config/');
  return response.data;
};

export const updatePricingConfig = async (data) => {
  const response = await api.put('/dynamic-pricing/api/pricing/config/', data);
  return response.data;
};

export const getPricingRules = async () => {
  const response = await api.get('/dynamic-pricing/api/pricing/rules/');
  return response.data;
};

export const createPricingRule = async (data) => {
  const response = await api.post('/dynamic-pricing/api/pricing/rules/', data);
  return response.data;
};

export const updatePricingRule = async (id, data) => {
  const response = await api.put(`/dynamic-pricing/api/pricing/rules/${id}/`, data);
  return response.data;
};

export const deletePricingRule = async (id) => {
  const response = await api.delete(`/dynamic-pricing/api/pricing/rules/${id}/`);
  return response.data;
};

export const getPricingEvents = async (params = {}) => {
  const response = await api.get('/dynamic-pricing/api/pricing/events/', { params });
  return response.data;
};

export const createPricingEvent = async (data) => {
  const response = await api.post('/dynamic-pricing/api/pricing/events/', data);
  return response.data;
};

export const updatePricingEvent = async (id, data) => {
  const response = await api.put(`/dynamic-pricing/api/pricing/events/${id}/`, data);
  return response.data;
};

export const deletePricingEvent = async (id) => {
  const response = await api.delete(`/dynamic-pricing/api/pricing/events/${id}/`);
  return response.data;
};

export const getPricingForecast = async (params) => {
  const response = await api.get('/dynamic-pricing/api/pricing/forecast/', { params });
  return response.data;
};

export const runPricingSimulation = async (payload) => {
  const response = await api.post('/dynamic-pricing/api/pricing/simulate/', payload);
  return response.data;
};

export const importCompetitorRates = async (payload) => {
  const response = await api.post('/dynamic-pricing/api/pricing/competitor/import/', payload);
  return response.data;
};

export const getCompetitorRates = async (params = {}) => {
  const response = await api.get('/dynamic-pricing/api/pricing/competitor/', { params });
  return response.data;
};

/** Discover nearby hotels (Places/OSM + Place Details); saves competitors without nightly rates until AI sync. */
export const autoDiscoverCompetitors = async (params = {}) => {
  const response = await api.post('/dynamic-pricing/api/pricing/competitor-hotels/auto-discover/', null, { params });
  return response.data;
};

/**
 * Refresh existing competitors only (no discovery).
 * @param {object} params - delay_seconds, rate_only (default true: Gemini AI rates; false: Places profile only)
 */
export const refreshExistingCompetitors = async (params = {}) => {
  const response = await api.post('/dynamic-pricing/api/pricing/competitor-hotels/refresh-existing/', null, { params });
  return response.data;
};

/** List competitor hotels (full list, no pagination). Used for Price comparison cards and map. */
export const getCompetitorHotels = async () => {
  const response = await api.get('/dynamic-pricing/api/pricing/competitor-hotels/');
  return response.data;
};

/** List competitor hotels with server-side pagination (for Competitor rate DataTable). Returns { Data, total_records }. */
export const getCompetitorHotelsList = async (params = {}) => {
  const response = await api.get('/dynamic-pricing/api/pricing/competitor-hotels/', { params });
  return response.data;
};

/** Create one competitor hotel row. */
export const createCompetitorHotel = async (payload) => {
  const response = await api.post('/dynamic-pricing/api/pricing/competitor-hotels/', payload);
  return response.data;
};

/** Sync one competitor hotel profile from Google Places only (no Travel / OCR). */
export const syncCompetitorHotel = async (id) => {
  const response = await api.post(`/dynamic-pricing/api/pricing/competitor-hotels/${id}/sync/`);
  return response.data;
};

/** Sync competitor nightly rate via Gemini AI (hotel currency, no Places profile update). */
export const syncCompetitorHotelRateOnly = async (id) => {
  const response = await api.post(
    `/dynamic-pricing/api/pricing/competitor-hotels/${id}/sync-rate/`,
    null,
    { timeout: 180000 },
  );
  return response.data;
};

/** Delete competitor hotel, daily rate history (CASCADE), and CompetitorRate rows for this name. */
export const deleteCompetitorHotel = async (id) => {
  await api.delete(`/dynamic-pricing/api/pricing/competitor-hotels/${id}/`);
};

/** Search competitor by name on Google Places and return autofill details. */
export const searchCompetitorByName = async (name, city = '') => {
  const response = await api.post('/dynamic-pricing/api/pricing/competitor-hotels/search-by-name/', { name, city });
  return response.data;
};

/** Set the selected hotel's latitude/longitude manually (e.g. when geocoding fails). */
export const setHotelLocation = async (latitude, longitude) => {
  const response = await api.post('/dynamic-pricing/api/pricing/set-hotel-location/', { latitude, longitude });
  return response.data;
};

/** Get latest our-hotel snapshot (AI rate + Places profile fields). */
export const getOurHotelGoogleSnapshot = async () => {
  const response = await api.get('/dynamic-pricing/api/pricing/our-hotel-google/');
  return response.data;
};

/** Google Places only: rating, reviews, photo, amenities on latest snapshot (no Travel / no nightly rate). */
export const syncOurHotelProfileFromPlaces = async () => {
  const response = await api.post('/dynamic-pricing/api/pricing/our-hotel-google/sync-profile/');
  return response.data;
};

/** Gemini AI: nightly rate for the selected property in hotel currency; updates latest snapshot rate fields. */
export const fetchOurHotelFromGoogle = async () => {
  const response = await api.post('/dynamic-pricing/api/pricing/our-hotel-google/fetch/');
  return response.data;
};

export const getPricingDashboard = async (params = {}) => {
  const response = await api.get('/dynamic-pricing/api/pricing/dashboard/', { params });
  return response.data;
};

export const getPricingHistory = async (params = {}) => {
  const response = await api.get('/dynamic-pricing/api/pricing/history/', { params });
  return response.data;
};

export const getPublishLogs = async (params = {}) => {
  const response = await api.get('/dynamic-pricing/api/pricing/publish-logs/', { params });
  return response.data;
};

/**
 * Get hotel configuration (timezone, country, currency)
 */
export const getHotelConfiguration = async () => {
  try {
    const response = await api.get('/onboarding/api/hotel/configuration/');
    return response.data;
  } catch (error) {
    // Error fetching hotel configuration:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update hotel configuration (timezone, country, currency)
 */
export const updateHotelConfiguration = async (configData) => {
  try {
    const response = await api.put('/onboarding/api/hotel/configuration/', configData);
    return response.data;
  } catch (error) {
    // Error updating hotel configuration:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get available configuration options (timezones, countries, currencies)
 */
export const getHotelConfigurationOptions = async () => {
  try {
    const response = await api.get('/onboarding/api/hotel/configuration/options/');
    return response.data;
  } catch (error) {
    // Error fetching configuration options:', error);
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

/** Cash Counter APIs */
export const getCashCounterSummary = async (params = {}) => {
  const response = await api.get('/finance/api/cash-counter/summary/', { params });
  return response.data;
};

export const getCashCounterLedger = async (params = {}) => {
  const response = await api.get('/finance/api/cash-counter/ledger/', { params });
  return response.data;
};

export const getCashCounterLedgerEntry = async (entryId) => {
  const response = await api.get(`/finance/api/cash-counter/ledger/${entryId}/`);
  return response.data;
};

export const updateCashCounterLedgerEntry = async (entryId, payload) => {
  const response = await api.patch(`/finance/api/cash-counter/ledger/${entryId}/`, payload);
  return response.data;
};

export const deleteCashCounterLedgerEntry = async (entryId) => {
  const response = await api.delete(`/finance/api/cash-counter/ledger/${entryId}/`);
  return response.data;
};

export const getCashCounterTimeline = async (params = {}) => {
  const response = await api.get('/finance/api/cash-counter/timeline/', { params });
  return response.data;
};

export const getCashCounterPayments = async (params = {}) => {
  const response = await api.get('/finance/api/cash-counter/payments/', { params });
  return response.data;
};

export const getCashCounterPrintData = async (params = {}) => {
  const response = await api.get('/finance/api/cash-counter/print/', { params });
  return response.data;
};

export const openCashCounter = async (payload) => {
  const response = await api.post('/finance/api/cash-counter/sessions/open/', payload);
  return response.data;
};

export const closeCashCounter = async (sessionId, payload) => {
  const response = await api.post(`/finance/api/cash-counter/sessions/${sessionId}/close/`, payload);
  return response.data;
};

export const withdrawCashCounter = async (payload) => {
  const response = await api.post('/finance/api/cash-counter/withdraw/', payload);
  return response.data;
};

export const convertCashCounterPayment = async (payload) => {
  const response = await api.post('/finance/api/cash-counter/convert-payment/', payload);
  return response.data;
};

/** Data Migration — validate uploaded CSV/Excel against hotel inventory (no writes).
 *  Basic parse runs first; full row validation only starts if the file is readable.
 *  Pass onProgress({ validated, total }) to receive live booking validation counts.
 */
export const validateDataMigrationFile = async (file, { onProgress } = {}) => {
  const formData = new FormData();
  formData.append('file', file);

  const headers = {
    ...getBearerTokenHeader(),
    Accept: 'application/x-ndjson, application/json',
  };
  const csrftoken = getCookie('csrftoken');
  if (csrftoken) {
    headers['X-CSRFToken'] = csrftoken;
  }

  const response = await fetch('/configuration/api/migration/validate/?stream=1', {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers,
  });

  const contentType = String(response.headers.get('content-type') || '');

  // Basic-parse failures (and other JSON errors) return before the stream starts.
  if (!response.ok || contentType.includes('application/json')) {
    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    if (!response.ok) {
      const err = new Error(data?.error || 'Validation failed.');
      err.response = { data, status: response.status };
      throw err;
    }
    if (typeof onProgress === 'function' && data?.total_records != null) {
      onProgress({
        validated: Number(data.total_records || 0),
        total: Number(data.total_records || 0),
      });
    }
    return data;
  }

  if (!response.body) {
    const err = new Error('Validation stream unavailable.');
    err.response = { data: { error: err.message, stage: 'validate' }, status: 500 };
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completePayload = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) continue;
      let event;
      try {
        event = JSON.parse(line);
      } catch {
        continue;
      }
      if (event.type === 'start' || event.type === 'progress') {
        if (typeof onProgress === 'function') {
          onProgress({
            validated: Number(event.validated || 0),
            total: Number(event.total || 0),
          });
        }
      } else if (event.type === 'complete') {
        completePayload = event;
        if (typeof onProgress === 'function') {
          onProgress({
            validated: Number(event.total_records || event.total || 0),
            total: Number(event.total_records || event.total || 0),
          });
        }
      } else if (event.type === 'error') {
        const err = new Error(event.error || 'Validation failed.');
        err.response = { data: event, status: 400 };
        throw err;
      }
    }
  }

  if (buffer.trim()) {
    try {
      const event = JSON.parse(buffer.trim());
      if (event.type === 'complete') completePayload = event;
      if (event.type === 'error') {
        const err = new Error(event.error || 'Validation failed.');
        err.response = { data: event, status: 400 };
        throw err;
      }
    } catch (parseErr) {
      if (parseErr?.response) throw parseErr;
    }
  }

  if (!completePayload) {
    const err = new Error('Validation ended without a result.');
    err.response = { data: { error: err.message, stage: 'validate' }, status: 500 };
    throw err;
  }

  const { type: _type, ...result } = completePayload;
  return result;
};

/** Data Migration — import valid rows as bookings with MG-YYYY-#### codes. */
export const importDataMigrationFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/configuration/api/migration/import/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Data Migration — list persisted import history for the selected hotel. */
export const fetchDataMigrationHistory = async () => {
  const response = await api.get('/configuration/api/migration/history/');
  return response.data;
};

/** Data Migration — fetch one history entry. */
export const fetchDataMigrationHistoryDetail = async (batchId) => {
  const response = await api.get(`/configuration/api/migration/history/${batchId}/`);
  return response.data;
};

/** Data Migration — delete history and only that batch's imported MG bookings (by stored IDs). */
export const deleteDataMigrationHistory = async (batchId) => {
  const response = await api.delete(`/configuration/api/migration/history/${batchId}/`);
  return response.data;
};

export default api;

